import { type PoGetSystemStatusResponse, poRefreshData } from '@repo/sdk/gen'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger
} from '@repo/ui/accordion'
import { DottedGlowBackground } from '@repo/ui/aceternity/dotted-glow-background'
import { StatusButton } from '@repo/ui/aceternity/stateful-button'
import { Button } from '@repo/ui/button'
import { Card } from '@repo/ui/card'
import { formatLocaleDate } from '@repo/ui/dates'
import { cn } from '@repo/ui/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	AlertTriangle,
	CheckCircle2,
	Clock3,
	Database,
	LoaderCircle,
	Monitor,
	RefreshCw,
	Unplug
} from 'lucide-react'
import { toast } from 'sonner'
import {
	datasetUpToDate,
	staleDays,
	updateInProgress
} from '@/lib/data-freshness'
import { type TKey, useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/lib/query-keys'
import { DatasetGaps } from '@/routes/(private)/documents/-components/dataset-notices'
import { poErrorMessage } from '@/routes/(private)/documents/-lib/error-message'
import { systemStatusQueryOptions } from '@/routes/(private)/documents/-lib/query-options'

export function MrBookConnection() {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	// The document list remains usable if this supporting status cannot load.
	const status = useQuery({
		...systemStatusQueryOptions(),
		refetchInterval: (query) =>
			query.state.data && updateInProgress(query.state.data)
				? 10_000
				: 30_000
	})
	const refresh = useMutation({
		mutationFn: () => poRefreshData({}),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.po.system()
			})
		},
		onError: (error) => toast.error(poErrorMessage(error))
	})
	const data = status.data
	const state = connectionState(data, status.isError, refresh.isPending)
	const presentation = CONNECTION_STATES[state]
	const busy =
		refresh.isPending || (data !== undefined && updateInProgress(data))
	const date = data?.dataset.asOf
		? formatLocaleDate(data.dataset.asOf, 'P HH:mm')
		: null

	return (
		<Card
			aria-label={t('po:connection.title')}
			className='relative gap-0 overflow-hidden py-0 shadow-xs'
			role='region'
		>
			<div className='pointer-events-none absolute inset-y-0 left-0 w-72 overflow-hidden opacity-35 [mask-image:linear-gradient(to_right,black,transparent)]'>
				<DottedGlowBackground
					colorVar='--primary'
					gap={14}
					glowColorVar='--primary'
					opacity={0.35}
					radius={1}
				/>
			</div>
			<div className='relative flex flex-wrap items-center gap-x-8 gap-y-4 p-5'>
				<div className='flex items-center gap-3 md:min-w-52'>
					<div className='flex size-11 shrink-0 items-center justify-center rounded-xl border border-primary/15 bg-background/90 text-primary shadow-xs'>
						<Monitor aria-hidden='true' className='size-5' />
					</div>
					<div>
						<h2 className='font-semibold text-lg tracking-tight'>
							Mr.Book
						</h2>
						<p className='text-muted-foreground text-xs'>
							{t('po:connection.subtitle')}
						</p>
					</div>
				</div>
				<div
					aria-live='polite'
					className='flex min-w-0 basis-full items-start gap-2.5 md:flex-1 md:basis-0 md:border-l md:pl-8'
				>
					<presentation.icon
						aria-hidden='true'
						className={cn(
							'mt-0.5 size-4 shrink-0',
							presentation.tone,
							(state === 'loading' || state === 'syncing') &&
								'motion-safe:animate-spin'
						)}
					/>
					<div className='min-w-0 space-y-1'>
						<p className='font-medium text-sm'>
							{t(presentation.label)}
						</p>
						{date && (
							<p className='text-muted-foreground text-xs tabular-nums'>
								{t('po:connection.available_data', { date })}
							</p>
						)}
						{state !== 'fresh' && (
							<p className='max-w-xl text-muted-foreground text-xs'>
								{t(
									data?.dataset.missingRequired.length &&
										[
											'empty',
											'partial',
											'incomplete'
										].includes(state)
										? 'po:connection.hints.blocked'
										: presentation.description
								)}
							</p>
						)}
					</div>
				</div>
				{state === 'unavailable' ? (
					<Button
						disabled={status.isFetching}
						onClick={() => status.refetch()}
						variant='outline'
					>
						<RefreshCw aria-hidden='true' />
						{t('po:connection.retry')}
					</Button>
				) : (
					<StatusButton
						aria-disabled={busy || undefined}
						className='shrink-0 bg-background'
						disabled={!data?.agent}
						onClick={() => refresh.mutate()}
						phase={busy ? 'loading' : 'idle'}
						variant='outline'
					>
						{!busy && <RefreshCw aria-hidden='true' />}
						{t(
							busy
								? 'po:connection.refresh_busy'
								: 'po:connection.refresh_now'
						)}
					</StatusButton>
				)}
			</div>
			{data && <ConnectionDetails data={data} />}
		</Card>
	)
}

function ConnectionDetails(props: { data: PoGetSystemStatusResponse['data'] }) {
	const { t } = useTranslation()
	const lastSeen = props.data.agent?.lastSeenAt
		? formatLocaleDate(props.data.agent.lastSeenAt, 'P HH:mm')
		: null
	const failed = props.data.tables.filter(
		(table) => table.lastOutcome === 'failed'
	)
	return (
		<Accordion className='relative border-border/60 border-t bg-muted/20'>
			<AccordionItem value='connection'>
				<AccordionTrigger
					aria-label={t('po:connection.details')}
					className='items-center gap-4 rounded-none px-5 py-2.5 hover:no-underline'
				>
					<span className='flex-1 font-normal text-muted-foreground text-xs'>
						{lastSeen
							? t('po:connection.last_seen', {
									date: lastSeen
								})
							: t('po:connection.no_contact')}
					</span>
				</AccordionTrigger>
				<AccordionContent className='space-y-4 px-5 pt-2 pb-4'>
					{props.data.agent && (
						<dl className='grid gap-4 text-xs sm:grid-cols-3'>
							<div className='space-y-1'>
								<dt className='text-muted-foreground'>
									{t('po:connection.computer')}
								</dt>
								<dd className='break-words font-medium'>
									{props.data.agent.name}
								</dd>
							</div>
							<div className='space-y-1'>
								<dt className='text-muted-foreground'>
									{t('po:connection.version')}
								</dt>
								<dd className='font-medium'>
									{props.data.agent.version ??
										t('po:connection.not_available')}
								</dd>
							</div>
							<div className='space-y-1'>
								<dt className='text-muted-foreground'>
									{t('po:connection.last_full_sync')}
								</dt>
								<dd className='font-medium tabular-nums'>
									{props.data.agent.lastFullSyncAt
										? formatLocaleDate(
												props.data.agent.lastFullSyncAt,
												'P HH:mm'
											)
										: t('po:connection.not_available')}
								</dd>
							</div>
						</dl>
					)}
					<DatasetGaps dataset={props.data.dataset} />
					{props.data.dataset.tablesBehind.map((table) => (
						<p className='text-xs' key={table.tableName}>
							{t('po:dataset.table_behind', {
								table: table.tableName,
								date: formatLocaleDate(
									table.confirmedAt,
									'P HH:mm'
								)
							})}
						</p>
					))}
					{failed.length > 0 && (
						<div className='space-y-2 text-xs'>
							<p>
								{t('po:index.update_failed', {
									list: failed
										.map((table) => table.tableName)
										.join(', ')
								})}
							</p>
							<dl className='space-y-2'>
								{failed.map((table) => (
									<div key={table.tableName}>
										<dt className='font-medium'>
											{table.tableName}
										</dt>
										<dd className='text-muted-foreground'>
											{table.lastFailureReason ??
												t(
													'po:connection.not_available'
												)}
										</dd>
									</div>
								))}
							</dl>
						</div>
					)}
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	)
}

function connectionState(
	data: PoGetSystemStatusResponse['data'] | undefined,
	unavailable: boolean,
	requestPending: boolean
) {
	if (unavailable) {
		return 'unavailable'
	}
	if (!data) {
		return 'loading'
	}
	if (!data.agent) {
		return 'unconfigured'
	}
	if (data.dataset.syncInProgress || data.refresh?.status === 'syncing') {
		return 'syncing'
	}
	if (requestPending || data.refresh?.status === 'queued') {
		return 'queued'
	}
	if (data.refresh?.status === 'stalled') {
		return 'stalled'
	}
	if (data.refresh?.status === 'incomplete') {
		return 'incomplete'
	}
	if (data.dataset.asOf === null) {
		return 'empty'
	}
	if (!datasetUpToDate(data)) {
		return 'partial'
	}
	if (
		staleDays(data.dataset.asOf) !== null ||
		data.tables.some((table) => staleDays(table.confirmedAt) !== null)
	) {
		return 'stale'
	}
	return 'fresh'
}

const CONNECTION_STATES = {
	loading: {
		label: 'po:connection.states.loading',
		description: 'po:connection.hints.loading',
		icon: LoaderCircle,
		tone: 'text-muted-foreground'
	},
	unavailable: {
		label: 'po:connection.states.unavailable',
		description: 'po:connection.hints.unavailable',
		icon: AlertTriangle,
		tone: 'text-foreground'
	},
	unconfigured: {
		label: 'po:connection.states.unconfigured',
		description: 'po:connection.hints.unconfigured',
		icon: Unplug,
		tone: 'text-muted-foreground'
	},
	queued: {
		label: 'po:connection.states.queued',
		description: 'po:connection.hints.queued',
		icon: Clock3,
		tone: 'text-muted-foreground'
	},
	syncing: {
		label: 'po:connection.states.syncing',
		description: 'po:connection.hints.syncing',
		icon: LoaderCircle,
		tone: 'text-muted-foreground'
	},
	stalled: {
		label: 'po:connection.states.stalled',
		description: 'po:connection.hints.stalled',
		icon: AlertTriangle,
		tone: 'text-foreground'
	},
	incomplete: {
		label: 'po:connection.states.incomplete',
		description: 'po:connection.hints.incomplete',
		icon: AlertTriangle,
		tone: 'text-foreground'
	},
	empty: {
		label: 'po:connection.states.empty',
		description: 'po:connection.hints.empty',
		icon: Database,
		tone: 'text-muted-foreground'
	},
	partial: {
		label: 'po:connection.states.partial',
		description: 'po:connection.hints.partial',
		icon: AlertTriangle,
		tone: 'text-foreground'
	},
	stale: {
		label: 'po:connection.states.stale',
		description: 'po:connection.hints.stale',
		icon: Clock3,
		tone: 'text-foreground'
	},
	fresh: {
		label: 'po:connection.states.fresh',
		description: 'po:connection.hints.fresh',
		icon: CheckCircle2,
		tone: 'text-chart-3'
	}
} satisfies Record<
	string,
	{ label: TKey; description: TKey; icon: typeof CheckCircle2; tone: string }
>
